import * as React from "react";
import { ComponentType } from "react";
import { connect } from "react-redux";
import { routerActions } from "react-router-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { InstitutionsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import RouterHoc, { IRouterActions, IRouterProps } from "src/hoc/RouterHoc";
import * as InstitutionActions from "../../../actions/institution";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { FormFilterInput } from "../../../components/form";
import {
    RestrictedLayout,
    TruncatedCell,
} from "../../../components/layouts/index";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";

import "./InstitutionsPage.scss";

interface IInstitutionsPageParams {}

interface IInstitutionsPageProps extends IRouterProps<IInstitutionsPageParams> {
    // From state
    formFilter: IFormFilter;
    formLength: number;
    institutions: Dtos.Institution[];

    loadingInstitutions: boolean;
    loadInstitutionsSuccess: boolean;
    loadInstitutionsFailure: boolean;
    loadInstitutionsErrorCode: string;

    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];

    pageSize: number;
}

interface IInstitutionsPageActions extends IRouterActions {
    clearInstitutions: InstitutionActions.IInstitutionClearActionCreator;
    loadInstitutions: InstitutionActions.IInstitutionLoadActionCreator;

    navigate: typeof routerActions.push;
    navigateReplace: typeof routerActions.replace;

    filter: InstitutionActions.IInstitutionFilterActionCreator;

    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;

    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;

}

type InstitutionsPageProps = IInstitutionsPageProps & IInstitutionsPageActions;

class InstitutionsPage extends React.PureComponent<InstitutionsPageProps, any> {
    constructor(props: InstitutionsPageProps) {
        super(props);

        this.clearInstitutions = this.clearInstitutions.bind(this);
    }

    componentDidMount() {
        const { 
            loadInstitutions, 
            setDrawerItems, 
            setBreadcrumbs
        } = this.props;
        

        setDrawerItems([]);
        setBreadcrumbs(InstitutionsBreadcrumbs());

        loadInstitutions();
    }

    componentWillReceiveProps(_nextProps: InstitutionsPageProps) {}

    componentDidUpdate(_prevProps: InstitutionsPageProps, _prevState) {
        const { loadInstitutionsFailure, navigate, loadInstitutionsErrorCode } =
            this.props;

        if (loadInstitutionsFailure) {
            navigate(`/error/${loadInstitutionsErrorCode}`);
        }
    }

    componentWillUpdate(_nextProps: InstitutionsPageProps) {}

    componentWillUnmount() {
        this.clearInstitutions();
    }

    clearInstitutions() {
        this.props.clearInstitutions();
    }

    showInstitutions(): boolean {
        const { loadingInstitutions, loadInstitutionsSuccess } = this.props;

        if (!loadingInstitutions && loadInstitutionsSuccess) {
            return true;
        }

        return false;
    }

    render() {
        const { loadingInstitutions } = this.props;

        return (
            <RestrictedLayout
                loading={loadingInstitutions}
                permission={Dtos.Permission.InstitutionView}
            >
                {this.renderContent()}
            </RestrictedLayout>
        );
    }

    renderContent() {
        const { permissions } = this.props;

        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Institutions</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={this.props.formFilter}
                            onFilter={(e) => this.props.filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.InstitutionCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/institutions/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!this.showInstitutions() ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {this.showInstitutions() ? (
                    <div className="card">
                        <div className="patient-tables">
                            {this.createTable()}
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }

    createTable() {
        const { institutions, pageSize, setPageSize } = this.props;

        if (!institutions || institutions.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no institutions to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = institutions.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={institutions}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : institutions.length
                    }
                    pageSize={showPagination ? pageSize : institutions.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no institutions to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string =
                                    "/institutions/" +
                                    rowInfo.original.id +
                                    "/details";
                                this.props.navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "name",
                            desc: false,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Duotone.INSTITUTION}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "name",
                            Header: "Name",
                            accessor: "name",
                            Cell: (props) => (
                                <TruncatedCell value={props.value} />
                            ),
                        },
                        {
                            id: "code",
                            Header: "Code",
                            accessor: "code",
                            maxWidth: 90,
                        },
                        {
                            id: "institutionType",
                            Header: "Type",
                            accessor: "institutionType.value",
                            maxWidth: 180,
                        },
                        {
                            id: "country",
                            Header: "Country",
                            accessor: "country.value",
                            maxWidth: 180,
                        },
                        {
                            id: "state",
                            Header: "State",
                            accessor: "state.value",
                            maxWidth: 220,
                        },
                    ]}
                />
            </div>
        );
    }
}

const mapStateToProps = (
    state: ITmdState,
    ownProps: InstitutionsPageProps
): IInstitutionsPageProps => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        history: ownProps.history,
        location: ownProps.location,
        match: ownProps.match,

        institutions: state.institutions.formFilteredData!,
        formFilter: state.institutions.formFilter,
        formLength: state.institutions.formLength,

        loadingInstitutions:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Pending,
        loadInstitutionsSuccess:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Success,
        loadInstitutionsFailure:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Failure,
        loadInstitutionsErrorCode: state.institutions.loadState.errorCode,

        user: state.user.data,
        permissions: permissions,

        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (
    dispatch,
    ownProps: InstitutionsPageProps
): IInstitutionsPageActions => {
    return {
        navigate: ownProps.navigate,
        navigateReplace: ownProps.navigateReplace,

        loadInstitutions: bindActionCreators(
            InstitutionActions.LoadInstitution,
            dispatch
        ),
        clearInstitutions: bindActionCreators(
            InstitutionActions.Clear,
            dispatch
        ),

        filter: bindActionCreators(InstitutionActions.Filter, dispatch),

        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),

        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default RouterHoc<IInstitutionsPageParams, InstitutionsPageProps>(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(InstitutionsPage) as unknown as ComponentType<InstitutionsPageProps>
) as ComponentType<any>;
